// src/utils/unifiedSessionManager.js
/**
 * Unified session manager for Curated Fika authentication
 * Provides persistent session storage shared across all Curated Fika pages
 */

const STORAGE_KEY = 'curated_fika_unified_session';
const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

class UnifiedSessionManager {
  /**
   * Check if user has any valid authenticated session
   */
  isAuthenticated() {
    const session = this.getSession();
    if (!session) return false;
    
    if (Date.now() > session.expiresAt) {
      this.clearSession();
      return false;
    }
    
    return true;
  }

  /**
   * Get current session data
   */
  getSession() {
    try {
      const sessionData = localStorage.getItem(STORAGE_KEY);
      if (!sessionData) return null;
      return JSON.parse(sessionData);
    } catch (error) {
      console.error('❌ Unified Session: Failed to retrieve session', error);
      this.clearSession();
      return null;
    }
  }

  /**
   * Save session data
   */
  saveSession(sessionData) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
      return true;
    } catch (error) {
      console.error('❌ Unified Session: Failed to save session', error);
      return false;
    }
  }

  // ============================================
  // MEMBER PORTAL METHODS
  // ============================================

  /**
   * Create Member Portal session after successful verification
   */
  createMemberSession(userApplication, verificationCode = null) {
    const existingSession = this.getSession() || {};
    
    const sessionData = {
      ...existingSession,
      memberPortal: {
        user: userApplication,
        verificationCode: verificationCode,
        authenticatedAt: Date.now()
      },
      email: userApplication.email,
      createdAt: existingSession.createdAt || Date.now(),
      expiresAt: Date.now() + SESSION_DURATION,
      lastActivity: Date.now()
    };
    
    this.saveSession(sessionData);
    console.log('✅ Unified Session: Member Portal session created');
    return sessionData;
  }

  /**
   * Check if Member Portal is authenticated
   */
  isMemberPortalAuthenticated() {
    if (!this.isAuthenticated()) return false;
    const session = this.getSession();
    return !!(session && session.memberPortal);
  }

  /**
   * Get Member Portal user application
   */
  getUserApplication() {
    if (!this.isMemberPortalAuthenticated()) return null;
    const session = this.getSession();
    return session.memberPortal.user;
  }

  // ============================================
  // MY SESSIONS METHODS
  // ============================================

  /**
   * Create My Sessions session after successful verification
   */
  createMySessionsSession(email, verificationCode, sessions) {
    const existingSession = this.getSession() || {};
    
    const sessionData = {
      ...existingSession,
      mySessions: {
        email,
        verificationCode,
        sessions,
        authenticatedAt: Date.now()
      },
      email: email,
      createdAt: existingSession.createdAt || Date.now(),
      expiresAt: Date.now() + SESSION_DURATION,
      lastActivity: Date.now()
    };
    
    this.saveSession(sessionData);
    console.log('✅ Unified Session: My Sessions session created');
    return sessionData;
  }

  /**
   * Check if My Sessions is authenticated
   */
  isMySessionsAuthenticated() {
    if (!this.isAuthenticated()) return false;
    const session = this.getSession();
    return !!(session && session.mySessions);
  }

  /**
   * Get My Sessions data
   */
  getMySessionsData() {
    if (!this.isMySessionsAuthenticated()) return null;
    const session = this.getSession();
    return session.mySessions;
  }

  /**
   * Update sessions data in My Sessions
   */
  updateMySessions(sessions) {
    const session = this.getSession();
    if (!session || !session.mySessions) return false;
    
    session.mySessions.sessions = sessions;
    session.lastActivity = Date.now();
    
    this.saveSession(session);
    return true;
  }

  // ============================================
  // SHARED METHODS
  // ============================================

  /**
   * Extend session expiry on user activity
   */
  extendSession() {
    const session = this.getSession();
    if (!session) return false;
    
    session.expiresAt = Date.now() + SESSION_DURATION;
    session.lastActivity = Date.now();
    
    this.saveSession(session);
    return true;
  }

  /**
   * Get remaining session time in minutes
   */
  getRemainingTime() {
    const session = this.getSession();
    if (!session) return 0;
    
    const remaining = session.expiresAt - Date.now();
    return Math.max(0, Math.ceil(remaining / 60000));
  }

  /**
   * Clear entire session (logout from all portals)
   */
  clearSession() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('🔓 Unified Session: Session cleared');
      return true;
    } catch (error) {
      console.error('❌ Unified Session: Failed to clear session', error);
      return false;
    }
  }

  /**
   * Check if session needs refresh warning
   */
  needsRefreshWarning() {
    return this.getRemainingTime() < 15;
  }

  /**
   * Get common email from any authenticated session
   */
  getEmail() {
    const session = this.getSession();
    return session?.email || null;
  }

  /**
   * Check if user has authenticated on ANY portal (used for cross-portal auto-fill)
   */
  hasAnyAuthentication() {
    return this.isMemberPortalAuthenticated() || this.isMySessionsAuthenticated();
  }
}

// Export singleton instance
const unifiedSessionManager = new UnifiedSessionManager();
export default unifiedSessionManager;